import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Loader2, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/chatbot`;

// Rotating greeting topics (used before /start returns one)
const INTRO_TOPICS = [
  'Curious about AI Agents? Ask how MetaxuOne agents triage and remediate incidents.',
  'Want to see our Datacenter playbook? Vendor-neutral DC compute, storage and backup.',
  'Exploring Observability? Learn how our context graph unifies your telemetry.',
  'Building for BFSI? Ask about regulated self-healing for banks and insurers.',
  'Need a quickstart? I can walk you through MetaxuOne in 30 minutes.',
  'Researching Self-Healing Ops? Ask about policy-gated remediation.',
];

const pickIntro = () => INTRO_TOPICS[Math.floor(Math.random() * INTRO_TOPICS.length)];
const isEmail = (v) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((v || '').trim());

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [awaitingEmail, setAwaitingEmail] = useState(true);
  const [welcomeBanner, setWelcomeBanner] = useState(null);
  const [messages, setMessages] = useState([]); // [{role:'user'|'assistant', text}]
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open, awaitingEmail]);

  // When the widget opens for the first time (no messages yet), seed the
  // rotating greeting + email prompt as the first assistant turn.
  const seedIntroIfNeeded = () => {
    if (messages.length > 0) return;
    const intro = pickIntro();
    setMessages([
      { role: 'assistant', text: `Hi — I'm the MetaxuOne AI Assistant. ${intro}` },
      { role: 'assistant', text: "Before we start, what's your business email?" },
    ]);
  };

  const resetAll = () => {
    setSessionId(null);
    setAwaitingEmail(true);
    setWelcomeBanner(null);
    setMessages([]);
    setInput('');
  };

  // Close + send transcript summary email
  const handleClose = async () => {
    setOpen(false);
    if (sessionId) {
      try {
        await fetch(`${API}/close`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId }),
          keepalive: true,
        });
      } catch (_) { /* swallow network errors on close */ }
    }
    setTimeout(resetAll, 250);
  };

  // Send summary if the user closes the tab mid-chat
  useEffect(() => {
    const onBeforeUnload = () => {
      if (sessionId) {
        try {
          const data = JSON.stringify({ session_id: sessionId });
          if (navigator.sendBeacon) {
            navigator.sendBeacon(`${API}/close`, new Blob([data], { type: 'application/json' }));
          }
        } catch (_) { /* ignore beacon errors */ }
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [sessionId]);

  const submitEmailInline = async (raw) => {
    const value = (raw || '').trim();
    setMessages((m) => [...m, { role: 'user', text: value }]);

    if (!isEmail(value)) {
      setMessages((m) => [...m, { role: 'assistant', text: "That doesn't look like a valid email. Please share your business email to start (e.g. you@yourcompany.com)." }]);
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: value, page: window.location.pathname }),
      });
      const data = await res.json();
      if (!res.ok) {
        // free-email blocked or other validation error — stay in email mode
        setMessages((m) => [...m, { role: 'assistant', text: data?.detail || "Couldn't validate that email. Please try a business email." }]);
        return;
      }
      setSessionId(data.session_id);
      setWelcomeBanner(data.welcome || null);
      setAwaitingEmail(false);
      setMessages((m) => [
        ...m,
        { role: 'assistant', text: `Thanks! You're all set. Go ahead — ask me anything about MetaxuOne.` },
      ]);
    } catch (_) {
      setMessages((m) => [...m, { role: 'assistant', text: 'Network issue. Please try again.' }]);
    } finally {
      setSending(false);
    }
  };

  const submitChatMessage = async (raw) => {
    const txt = (raw || '').trim();
    if (!txt || !sessionId) return;
    setMessages((m) => [...m, { role: 'user', text: txt }]);
    setSending(true);
    try {
      const res = await fetch(`${API}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: txt }),
      });
      const data = await res.json();
      if (!res.ok) {
        setMessages((m) => [...m, { role: 'assistant', text: data?.detail || 'Something went wrong. Try again?' }]);
      } else {
        setMessages((m) => [...m, { role: 'assistant', text: data.reply }]);
      }
    } catch (_) {
      setMessages((m) => [...m, { role: 'assistant', text: 'Network issue. Please retry.' }]);
    } finally {
      setSending(false);
    }
  };

  const onSubmit = (e) => {
    e?.preventDefault?.();
    const value = input;
    if (!value.trim() || sending) return;
    setInput('');
    if (awaitingEmail) submitEmailInline(value);
    else submitChatMessage(value);
  };

  return (
    <>
      {/* Floating launcher — icon only */}
      {!open && (
        <button
          onClick={() => { setOpen(true); seedIntroIfNeeded(); }}
          data-testid="chatbot-launcher"
          className="fixed bottom-6 right-6 z-[60] inline-flex items-center justify-center w-12 h-12 rounded-full bg-white text-neutral-950 shadow-[0_10px_30px_-10px_rgba(59,130,246,0.5)] border border-neutral-200 hover:bg-neutral-100 transition-colors"
          aria-label="Open chat"
        >
          <span className="relative inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-500/15 border border-blue-500/40 text-blue-600">
            <MessageCircle className="w-4 h-4" />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
          </span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-6 right-6 z-[60] w-[min(92vw,380px)] h-[min(78vh,580px)] rounded-2xl bg-neutral-950 border border-neutral-800 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] flex flex-col overflow-hidden"
          role="dialog"
          aria-label="MetaxuOne chat"
        >
          {/* Header */}
          <div className="relative px-4 py-3 border-b border-neutral-800 bg-gradient-to-b from-neutral-900 to-neutral-950">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-blue-500/15 border border-blue-500/40 text-blue-300 inline-flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <div className="text-[13.5px] font-semibold text-white truncate">MetaxuOne AI Assistant</div>
                <div className="text-[11.5px] text-neutral-400 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Online
                </div>
              </div>
              <button
                onClick={handleClose}
                aria-label="Close chat"
                data-testid="chatbot-close-btn"
                className="ml-auto w-8 h-8 rounded-full border border-neutral-800 text-neutral-300 hover:text-white hover:border-neutral-600 inline-flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Topic banner — only after session starts */}
          {welcomeBanner && (
            <div className="px-4 py-3 border-b border-neutral-900 bg-neutral-900/40">
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-blue-400">Today&apos;s topic</div>
              <div className="mt-0.5 text-[13.5px] font-medium text-white leading-snug">{welcomeBanner.title}</div>
            </div>
          )}

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chatbot-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  data-testid={`chatbot-msg-${m.role}`}
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13.5px] leading-relaxed whitespace-pre-wrap break-words ${
                    m.role === 'user'
                      ? 'bg-blue-500 text-white rounded-br-sm'
                      : 'bg-neutral-900 text-neutral-100 border border-neutral-800 rounded-bl-sm'
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-neutral-900 border border-neutral-800 rounded-2xl rounded-bl-sm px-3.5 py-2.5">
                  <div className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-neutral-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-neutral-500 animate-bounce" style={{ animationDelay: '120ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-neutral-500 animate-bounce" style={{ animationDelay: '240ms' }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Composer — same input is used for the email step and the chat step */}
          <form onSubmit={onSubmit} className="border-t border-neutral-800 bg-neutral-950 px-3 py-3 flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSubmit(); }
              }}
              placeholder={awaitingEmail ? 'Type your business email…' : 'Ask anything about MetaxuOne…'}
              rows={1}
              data-testid={awaitingEmail ? 'chatbot-email-input' : 'chatbot-input'}
              autoComplete={awaitingEmail ? 'email' : 'off'}
              inputMode={awaitingEmail ? 'email' : 'text'}
              className="flex-1 max-h-28 resize-none rounded-xl bg-black border border-neutral-800 focus:border-blue-500/60 focus:outline-none text-white text-[13.5px] placeholder:text-neutral-600 px-3 py-2.5"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              data-testid="chatbot-send-btn"
              className="w-10 h-10 rounded-full bg-white text-neutral-950 hover:bg-neutral-200 inline-flex items-center justify-center disabled:opacity-50"
              aria-label="Send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
